/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { CountryBannerItem, HotelBannerItem } from '../../types';
import { BANNER_HEIGHT, DEFAULT } from '../../configs/constants';

// Local fallback banner asset - no network needed
const DEFAULT_BANNER_IMAGE = require('../../assets/images/banner-placeholder.jpg');

/**
 * Get default banner using local asset
 * Used for fallback (on error/empty)
 * No redirection URL, no network call needed
 */
export const getDefaultBanner = (): HotelBannerItem => ({
  id: 'default-banner',
  name: 'Default Banner',
  url: DEFAULT_BANNER_IMAGE,
  // No redirectionUrl - banner should not be clickable
});

/**
 * Extract banner name from URL
 * @param url - Banner image URL
 */
export const getBannerNameFromUrl = (url: string | number): string => {
  if (typeof url !== 'string') return '';
  const parts = url.split('/');
  const filename = parts[parts.length - 1];
  return filename?.split('.')?.[0] || '';
};

/**
 * Attempt to sanitize a malformed JSON array string.
 * Handles common server-side issues like `{  {` (duplicate opening braces)
 * by extracting valid JSON objects and reconstructing an array.
 */
export const sanitizeJsonString = (jsonString: string): string => {
  try {
    JSON.parse(jsonString);
    return jsonString;
  } catch {
    const objects: string[] = [];
    const regex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
    let match;
    while ((match = regex.exec(jsonString)) !== null) {
      try {
        JSON.parse(match[0]);
        objects.push(match[0]);
      } catch {
        // skip malformed object
      }
    }
    return objects.length > 0 ? `[${objects.join(',')}]` : jsonString;
  }
};

/**
 * Safely parse JSON string to typed array
 * Returns empty array if parsing fails.
 * Attempts to sanitize malformed JSON before parsing.
 */
export const parseJsonArray = <T>(jsonString: string): T[] => {
  try {
    const sanitized = sanitizeJsonString(jsonString);
    return JSON.parse(sanitized);
  } catch {
    return [];
  }
};

/**
 * Check if banner is active based on date range.
 * Uses local timezone for consistent date comparison.
 */
export const isBannerActive = (startDate: string, endDate: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);

  return today >= start && today <= end;
};

/**
 * Transform raw banner data to HotelBannerItem format
 * @param banners - Raw banner items from API
 * @param validateDates - If true, filter by startDate/endDate. Default banners skip validation.
 */
export const transformBannerData = (
  banners: CountryBannerItem[],
  validateDates = true,
): HotelBannerItem[] => {
  const filteredBanners = banners.filter((banner) => {
    if (!banner.bannerUrl) return false;
    if (!validateDates) return true;
    if (!banner.startDate || !banner.endDate) return false;
    return isBannerActive(banner.startDate, banner.endDate);
  });

  return filteredBanners.map((banner, index) => {
    const bannerName = getBannerNameFromUrl(banner.bannerUrl);
    return {
      id: `${bannerName}-${index}`,
      name: bannerName,
      url: banner.bannerUrl,
      height: BANNER_HEIGHT,
      width: undefined,
      redirectionUrl: banner.redirectionUrl,
    };
  });
};

/**
 * Parse banner data from API response
 * @param bannerData - Raw banner data from API
 * @param countryCode - Country code
 */
export const parseBannerData = (
  bannerData: any,
  countryCode?: string,
): HotelBannerItem[] => {
  const banner = bannerData?.banner;
  if (!banner) return [];

  // Try country-specific banners with date validation
  if (countryCode) {
    const normalizedCountryCode = countryCode.trim();
    const countryBannerData =
      banner[normalizedCountryCode] ??
      banner[normalizedCountryCode.toLowerCase()] ??
      banner[normalizedCountryCode.toUpperCase()];
    if (countryBannerData) {
      const parsedCountryBanners = Array.isArray(countryBannerData)
        ? (countryBannerData as CountryBannerItem[])
        : parseJsonArray<CountryBannerItem>(countryBannerData);
      const activeBanners = transformBannerData(parsedCountryBanners, true);
      if (activeBanners.length > 0) return activeBanners;
    }
  }

  // Fallback to default banners (no date validation)
  const defaultBannerData = banner[DEFAULT];
  return defaultBannerData
    ? transformBannerData(
        Array.isArray(defaultBannerData)
          ? (defaultBannerData as CountryBannerItem[])
          : parseJsonArray<CountryBannerItem>(defaultBannerData),
        false,
      )
    : [];
};
